
import { useState, useEffect } from 'react';
/* eslint-disable react-hooks/exhaustive-deps -- modal loaders run only when opening or changing branch */
import { supabase } from '../../../lib/supabase';
import { useSucursal } from '../../../utils/sucursalContext';

interface Usuario {
  id: string;
  nombre: string | null;
  email: string | null;
  telefono: string | null;
  tipo_membresia: string | null;
  estado: string | null;
  fecha_vencimiento?: string | null;
}

interface Plan {
  id: string;
  nombre: string;
  precio: string | number;
  duracion_dias: number;
  max_sesiones_periodo?: number | null;
  activo: boolean;
}

interface AbonoForm {
  id: string;
  monto: string;
  metodo_pago: string;
  banco: string;
  numero_comprobante: string;
  fecha_pago: string;
  notas: string;
}

interface PendingFlowInvoice {
  invoice_id: string;
  amount: number;
  subject: string;
  next_attempt_date: string | null;
  subscription_id: string;
}

interface PaidFlowInvoice {
  invoice_id: string;
  amount: number;
  subject: string;
  subscription_id: string;
  membership_will_be_preserved?: boolean;
  membership_end?: string | null;
}

interface RegistrarPagoModalProps {
  onClose: () => void;
  onPagoRegistrado: () => void;
  metodosPagoDisponibles?: Array<{
    value: string;
    label: string;
  }>;
}

export default function RegistrarPagoModal({
  onClose,
  onPagoRegistrado,
  metodosPagoDisponibles,
}: RegistrarPagoModalProps) {
  const { sucursalActual } = useSucursal();
  const [usuarios, setUsuarios] = useState<Usuario[]>([]);
  const [planes, setPlanes] = useState<Plan[]>([]);
  const [usuarioSeleccionado, setUsuarioSeleccionado] = useState<Usuario | null>(null);
  const [busquedaUsuario, setBusquedaUsuario] = useState('');
  const [mostrarListaUsuarios, setMostrarListaUsuarios] = useState(false);
  const [loading, setLoading] = useState(false);
  const [guardando, setGuardando] = useState(false);
  const [notaVencimientoAuto, setNotaVencimientoAuto] = useState('');
  const [tipoRegistro, setTipoRegistro] = useState<'membresia' | 'cambio_plan'>('membresia');
  const [planDestinoId, setPlanDestinoId] = useState('');
  const [usarAbonos, setUsarAbonos] = useState(false);
  const [flowPendiente, setFlowPendiente] = useState<PendingFlowInvoice | null>(null);
  const [flowPagado, setFlowPagado] = useState<PaidFlowInvoice | null>(null);
  const [consultandoFlow, setConsultandoFlow] = useState(false);
  const [conciliandoPagoFlow, setConciliandoPagoFlow] = useState(false);
  const [conciliarFlow, setConciliarFlow] = useState(true);

  const metodosPago = metodosPagoDisponibles ?? [];

  const fechaHoyChile = () => new Intl.DateTimeFormat('en-CA', {
    timeZone: 'America/Santiago',
    year: 'numeric',
    month: '2-digit',
    day: '2-digit',
  }).format(new Date());

  const nuevoAbono = (monto = ''): AbonoForm => ({
    id: `${Date.now()}-${Math.random().toString(36).slice(2)}`,
    monto,
    metodo_pago: metodosPago[0]?.value || '',
    banco: '',
    numero_comprobante: '',
    fecha_pago: fechaHoyChile(),
    notas: '',
  });

  const [abonos, setAbonos] = useState<AbonoForm[]>([nuevoAbono()]);

  const [formData, setFormData] = useState({
    plan: '',
    monto: '',
    metodo_pago: metodosPago[0]?.value || '',
    banco: '',
    numero_comprobante: '',
    fecha_pago: fechaHoyChile(),
    estado: 'Completado',
    notas: ''
  });

  const bancos = [
    'Banco de Chile',
    'Banco Estado',
    'Banco Santander',
    'Banco BCI',
    'Banco Scotiabank',
    'Banco Itaú',
    'Banco Security',
    'Banco Falabella',
    'Banco Ripley',
    'Banco Consorcio',
    'Banco BICE',
    'Banco Internacional',
    'Otro'
  ];

  const normalizePlanName = (value: string | null | undefined) =>
    String(value || '').trim().toLocaleLowerCase('es-CL');

  const planActual = usuarioSeleccionado
    ? planes.find((plan) => normalizePlanName(plan.nombre) === normalizePlanName(usuarioSeleccionado.tipo_membresia)) || null
    : null;
  const planDestino = planes.find((plan) => plan.id === planDestinoId) || null;
  const diferenciaCambioPlan = planActual && planDestino
    ? Number(planDestino.precio) - Number(planActual.precio)
    : 0;
  const planMembresia = planes.find((plan) => plan.nombre === formData.plan) || null;
  const totalAbonado = abonos.reduce((total, abono) => total + (Number(abono.monto) || 0), 0);
  const totalEsperado = Number(planMembresia?.precio || 0);
  const saldoPendiente = Math.max(0, totalEsperado - totalAbonado);

  useEffect(() => {
    cargarUsuarios();
    cargarPlanes();
  }, [sucursalActual]);

  useEffect(() => {
    if (!metodosPago.length) {
      setFormData((prev) => ({ ...prev, metodo_pago: '' }));
      setAbonos((prev) => prev.map((abono) => ({ ...abono, metodo_pago: '' })));
      return;
    }
    const metodoActualEsValido = metodosPago.some((metodo) => metodo.value === formData.metodo_pago);
    if (!metodoActualEsValido) {
      setFormData((prev) => ({ ...prev, metodo_pago: metodosPago[0].value }));
    }
    setAbonos((prev) => prev.map((abono) => (
      metodosPago.some((metodo) => metodo.value === abono.metodo_pago)
        ? abono
        : { ...abono, metodo_pago: metodosPago[0].value }
    )));
  }, [metodosPago, formData.metodo_pago]);

  const cargarPlanes = async () => {
    try {
      const gimnasioId = sucursalActual?.gimnasio_id;
      if (!gimnasioId) {
        setPlanes([]);
        return;
      }

      const { data, error } = await supabase
        .from('planes')
        .select('id, nombre, precio, duracion_dias, max_sesiones_periodo, activo')
        .eq('gimnasio_id', gimnasioId)
        .eq('activo', true)
        .order('precio');

      if (error) throw error;

      setPlanes(data || []);
    } catch (error) {
      console.error('Error al cargar planes:', error);
    }
  };

  const cargarUsuarios = async () => {
    if (!sucursalActual?.id) {
      setUsuarios([]);
      setLoading(false);
      return;
    }

    try {
      setLoading(true);
      const query = supabase
        .from('usuarios')
        .select('id, nombre, email, telefono, tipo_membresia, estado, fecha_vencimiento')
        .eq('sucursal_id', sucursalActual.id)
        .order('nombre');

      const { data, error } = await query;

      if (error) throw error;

      setUsuarios(data || []);
    } catch (error) {
      console.error('Error al cargar usuarios:', error);
    } finally {
      setLoading(false);
    }
  };

  const usuariosFiltrados = usuarios.filter((usuario) => {
    const search = busquedaUsuario.toLowerCase().trim();
    const nombre = String(usuario.nombre || '').toLowerCase();
    const email = String(usuario.email || '').toLowerCase();
    const telefono = String(usuario.telefono || '');

    return nombre.includes(search) || email.includes(search) || telefono.includes(search);
  });

  const cargarCobroFlowPendiente = async (usuarioId: string) => {
    setConsultandoFlow(true);
    setFlowPendiente(null);
    setFlowPagado(null);
    setConciliarFlow(true);
    try {
      const { data, error } = await supabase.functions.invoke('flow-admin-outside-payment', {
        body: { action: 'inspect', usuario_id: usuarioId },
      });
      if (error) throw error;
      if (data?.pending && data?.invoice) setFlowPendiente(data.invoice as PendingFlowInvoice);
      if (data?.provider_paid && data?.invoice) setFlowPagado(data.invoice as PaidFlowInvoice);
    } catch (error) {
      console.error('No se pudo consultar el cobro pendiente en Flow:', error);
    } finally {
      setConsultandoFlow(false);
    }
  };

  const conciliarPagoRealizadoEnFlow = async () => {
    if (!usuarioSeleccionado || !flowPagado || conciliandoPagoFlow) return;
    const confirmado = window.confirm(
      `Flow informa pagada la factura ${flowPagado.invoice_id} por $${Number(flowPagado.amount).toLocaleString('es-CL')}.\n\n` +
      (flowPagado.membership_will_be_preserved
        ? `¿Deseas registrar el pago en Mowe? La vigencia actual hasta el ${flowPagado.membership_end?.split('-').reverse().join('-')} se conservará sin agregar días.`
        : '¿Deseas crear el pago en Mowe y activar la membresía?') +
      ' Esta operación no realizará un nuevo cobro.'
    );
    if (!confirmado) return;
    try {
      setConciliandoPagoFlow(true);
      const { data, error } = await supabase.functions.invoke('flow-admin-outside-payment', {
        body: { action: 'sync_provider_payment', usuario_id: usuarioSeleccionado.id },
      });
      if (error || !data?.reconciled) throw new Error(data?.error || error?.message || 'Flow no confirmó la conciliación.');
      alert(data.message || 'Pago Flow conciliado correctamente.');
      setFlowPagado(null);
      onPagoRegistrado();
      onClose();
    } catch (error) {
      const message = error instanceof Error ? error.message : 'No se pudo conciliar el pago Flow.';
      alert(message);
    } finally {
      setConciliandoPagoFlow(false);
    }
  };

  const handleSeleccionarUsuario = (usuario: Usuario) => {
    const notaVenc = usuario.fecha_vencimiento
      ? `Fecha de vencimiento: ${usuario.fecha_vencimiento.split('-').reverse().join('-')}`
      : 'Fecha de vencimiento: -';

    setUsuarioSeleccionado(usuario);
    setTipoRegistro('membresia');
    setPlanDestinoId('');
    setUsarAbonos(false);
    setBusquedaUsuario('');
    setMostrarListaUsuarios(false);
    setNotaVencimientoAuto(notaVenc);
    void cargarCobroFlowPendiente(usuario.id);

    // Autocompletar plan y monto según el tipo de membresía del usuario
    const planUsuario = planes.find(p => 
      p.nombre.toLowerCase().includes(usuario.tipo_membresia?.toLowerCase() || '')
    );

    if (planUsuario) {
      setFormData(prev => ({
        ...prev,
        plan: planUsuario.nombre,
        monto: String(planUsuario.precio)
      }));
      setAbonos([nuevoAbono(String(planUsuario.precio))]);
    } else {
      setFormData(prev => ({
        ...prev,
        plan: usuario.tipo_membresia || '',
        monto: ''
      }));
      setAbonos([nuevoAbono()]);
    }
  };

  const handlePlanChange = (planNombre: string) => {
    const planSeleccionado = planes.find(p => p.nombre === planNombre);
    
    setFormData(prev => ({
      ...prev,
      plan: planNombre,
      monto: planSeleccionado ? String(planSeleccionado.precio) : prev.monto
    }));
    if (usarAbonos) {
      setAbonos([nuevoAbono(planSeleccionado ? String(planSeleccionado.precio) : '')]);
    }
  };

  const handleTipoRegistroChange = (next: 'membresia' | 'cambio_plan') => {
    setTipoRegistro(next);
    setPlanDestinoId('');
    setUsarAbonos(false);
    if (next === 'cambio_plan') {
      setFormData((prev) => ({ ...prev, plan: '', monto: '', estado: 'Completado' }));
      return;
    }
    if (planActual) {
      setFormData((prev) => ({
        ...prev,
        plan: planActual.nombre,
        monto: String(planActual.precio),
      }));
    }
  };

  const handleUsarAbonos = (activo: boolean) => {
    setUsarAbonos(activo);
    if (activo) {
      setAbonos([nuevoAbono(planMembresia ? String(planMembresia.precio) : formData.monto)]);
    }
  };

  const actualizarAbono = (id: string, cambios: Partial<AbonoForm>) => {
    setAbonos((prev) => prev.map((abono) => abono.id === id ? { ...abono, ...cambios } : abono));
  };

  const quitarAbono = (id: string) => {
    setAbonos((prev) => prev.length > 1 ? prev.filter((abono) => abono.id !== id) : prev);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!usuarioSeleccionado) {
      alert('Por favor selecciona un usuario');
      return;
    }

    if (!sucursalActual?.id) {
      alert('Debes seleccionar una sucursal activa para registrar pagos');
      return;
    }

    if (tipoRegistro === 'cambio_plan' && (!planDestino || diferenciaCambioPlan <= 0)) {
      alert('Selecciona un plan superior que genere una diferencia por cobrar');
      return;
    }

    if (tipoRegistro === 'membresia' && !usarAbonos && (!formData.monto || Number(formData.monto) <= 0)) {
      alert('Por favor ingresa un monto válido');
      return;
    }

    if (tipoRegistro === 'membresia' && usarAbonos) {
      if (!planMembresia || totalEsperado <= 0) {
        alert('Selecciona un plan válido para registrar sus abonos');
        return;
      }
      if (abonos.some((abono) => !abono.monto || Number(abono.monto) <= 0 || !abono.fecha_pago)) {
        alert('Cada voucher o abono debe tener monto y fecha válidos');
        return;
      }
      if (totalAbonado > totalEsperado) {
        alert(`Los abonos superan el valor del plan en $${(totalAbonado - totalEsperado).toLocaleString('es-CL')}`);
        return;
      }
    }

    if (!(tipoRegistro === 'membresia' && usarAbonos) && !metodosPago.some((metodo) => metodo.value === formData.metodo_pago)) {
      alert('No hay metodos de pago habilitados en Configuracion > Metodos de Pago.');
      return;
    }

    try {
      setGuardando(true);

      const debeConciliarFlow = tipoRegistro === 'membresia' && !usarAbonos &&
        formData.estado.toLowerCase() === 'completado' && !!flowPendiente && conciliarFlow;
      if (debeConciliarFlow) {
        const montoCoincide = Math.round(Number(formData.monto)) === Math.round(Number(flowPendiente.amount));
        if (!montoCoincide) {
          alert(`Para conciliar Flow, el monto debe ser exactamente $${Number(flowPendiente.amount).toLocaleString('es-CL')}.`);
          return;
        }
        const confirmado = window.confirm(
          `Existe un cobro automático pendiente en Flow por $${Number(flowPendiente.amount).toLocaleString('es-CL')}.\n\n` +
          '¿Deseas informarlo como pagado por otro medio para evitar nuevos intentos?\n\n' +
          'La tarjeta seguirá inscrita para el próximo período.'
        );
        if (!confirmado) return;

        // Revalidar inmediatamente antes de registrar el pago reduce el riesgo
        // de que Flow haya cobrado la factura mientras el modal estaba abierto.
        const { data: estadoActualFlow, error: recheckError } = await supabase.functions.invoke('flow-admin-outside-payment', {
          body: { action: 'inspect', usuario_id: usuarioSeleccionado.id },
        });
        if (recheckError) {
          alert('No pudimos volver a comprobar la factura en Flow. No se registró el pago; intenta nuevamente para evitar un cobro duplicado.');
          return;
        }
        if (!estadoActualFlow?.pending || estadoActualFlow?.invoice?.invoice_id !== flowPendiente.invoice_id) {
          alert('La factura cambió de estado en Flow mientras registrabas el pago. No se registró nada; actualiza y revisa la suscripción antes de continuar.');
          return;
        }
      }

      const gimnasioId = sucursalActual.gimnasio_id;
      const { data: branchConfig, error: branchError } = await supabase
        .from('pago_configuracion').select('efectivo,tarjeta_credito,tarjeta_debito,transferencia,otro')
        .eq('gimnasio_id', gimnasioId).eq('sucursal_id', sucursalActual.id).maybeSingle();
      if (branchError) throw branchError;
      const { data: gymConfig, error: gymError } = branchConfig ? { data: null, error: null } : await supabase
        .from('pago_configuracion').select('efectivo,tarjeta_credito,tarjeta_debito,transferencia,otro')
        .eq('gimnasio_id', gimnasioId).is('sucursal_id', null).maybeSingle();
      if (gymError) throw gymError;
      const paymentConfig = branchConfig || gymConfig;
      const enabledMethods = new Set<string>();
      if (paymentConfig?.efectivo === true) enabledMethods.add('efectivo');
      if (paymentConfig?.tarjeta_credito === true || paymentConfig?.tarjeta_debito === true) enabledMethods.add('tarjeta');
      if (paymentConfig?.transferencia === true) enabledMethods.add('transferencia');
      if (paymentConfig?.otro === true) enabledMethods.add('otro');
      const metodosSeleccionados = tipoRegistro === 'membresia' && usarAbonos
        ? abonos.map((abono) => abono.metodo_pago)
        : [formData.metodo_pago];
      if (metodosSeleccionados.some((metodo) => !enabledMethods.has(metodo))) {
        alert('Este medio de pago ya no está habilitado para la sucursal. Actualiza la configuración antes de registrar el pago.');
        return;
      }

      if (tipoRegistro === 'membresia' && usarAbonos) {
        const { error: abonosError } = await supabase.rpc('registrar_pago_membresia_abonos_admin', {
          p_usuario_id: usuarioSeleccionado.id,
          p_plan_id: planMembresia!.id,
          p_abonos: abonos.map((abono) => ({
            monto: Number(abono.monto),
            metodo_pago: abono.metodo_pago,
            fecha_pago: abono.fecha_pago,
            banco: abono.metodo_pago === 'transferencia' ? abono.banco || null : null,
            numero_comprobante: abono.numero_comprobante || null,
            notas: abono.notas || null,
          })),
          p_notas: formData.notas || null,
        });
        if (abonosError) throw abonosError;
        onPagoRegistrado();
        onClose();
        return;
      }

      if (tipoRegistro === 'cambio_plan') {
        const { error: changeError } = await supabase.rpc('registrar_cambio_plan_diferencia_admin', {
          p_usuario_id: usuarioSeleccionado.id,
          p_plan_destino_id: planDestino!.id,
          p_metodo_pago: formData.metodo_pago,
          p_fecha_pago: formData.fecha_pago,
          p_banco: formData.banco || null,
          p_numero_comprobante: formData.numero_comprobante || null,
          p_notas: formData.notas || null,
        });
        if (changeError) throw changeError;
        onPagoRegistrado();
        onClose();
        return;
      }

      const planSeleccionado = planes.find((p) => p.nombre === formData.plan);
      const estadoNormalizado = formData.estado.toLowerCase();

      const pagoData = {
        usuario_id: usuarioSeleccionado.id,
        monto: Number(formData.monto),
        metodo_pago: formData.metodo_pago,
        concepto: `Pago de membresía - ${formData.plan}`,
        fecha_pago: formData.fecha_pago,
        estado: estadoNormalizado,
        sucursal_id: sucursalActual.id,
        banco: formData.banco || null,
        numero_comprobante: formData.numero_comprobante || null,
        notas: formData.notas || null
      };

      const { data: pagoRegistrado, error } = await supabase
        .from('pagos')
        .insert([pagoData])
        .select('id')
        .single();

      if (error) throw error;

      if (estadoNormalizado === 'completado' && planSeleccionado) {
        const { error: applyError } = await supabase.rpc('aplicar_plan_pagado_admin', {
          p_usuario_id: usuarioSeleccionado.id,
          p_plan_nombre: planSeleccionado.nombre,
          p_duracion_dias: Number(planSeleccionado.duracion_dias),
        });
        if (applyError) throw applyError;
      }

      if (debeConciliarFlow) {
        const { data: conciliacion, error: conciliacionError } = await supabase.functions.invoke('flow-admin-outside-payment', {
          body: {
            action: 'reconcile',
            usuario_id: usuarioSeleccionado.id,
            pago_id: pagoRegistrado.id,
          },
        });
        if (conciliacionError || !conciliacion?.reconciled) {
          const detalle = conciliacion?.error || conciliacionError?.message || 'Flow no confirmó la conciliación.';
          alert(
            `El pago quedó registrado correctamente y la vigencia se actualizó, pero la factura de Flow requiere revisión.\n\n${detalle}\n\n` +
            'No registres otro pago. Revisa la suscripción Flow antes de su próximo reintento.'
          );
          onPagoRegistrado();
          onClose();
          return;
        }
        alert(conciliacion.message || 'Pago registrado y cobro pendiente de Flow conciliado correctamente.');
      }

      onPagoRegistrado();
      onClose();
    } catch (error) {
      console.error('Error al registrar pago:', error);
      const message = error instanceof Error
        ? error.message
        : typeof (error as any)?.message === 'string'
          ? (error as any).message
          : 'Error al registrar el pago. Por favor intenta nuevamente.';
      alert(message);
    } finally {
      setGuardando(false);
    }
  };

  const metodosRequierenBanco = ['transferencia'];

  return (
    <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="px-8 py-6 border-b border-gray-100">
          <div className="flex items-start justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-900">Registrar Pago Manual</h2>
              <p className="text-sm text-gray-500 mt-1">Registra pagos por transferencia, efectivo u otros medios</p>
            </div>
            <button
              onClick={onClose}
              className="w-10 h-10 flex items-center justify-center text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <i className="ri-close-line text-2xl"></i>
            </button>
          </div>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="flex-1 overflow-y-auto px-8 py-6">
          <div className="space-y-5">
            {/* Selección de Usuario */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Usuario <span className="text-red-500">*</span>
              </label>
              
              {!usuarioSeleccionado ? (
                <div className="relative">
                  <div className="relative">
                    <i className="ri-search-line absolute left-4 top-1/2 -translate-y-1/2 text-gray-400 text-lg"></i>
                    <input
                      type="text"
                      placeholder="Buscar por nombre, email o teléfono..."
                      value={busquedaUsuario}
                      onChange={(e) => {
                        setBusquedaUsuario(e.target.value);
                        setMostrarListaUsuarios(true);
                      }}
                      onFocus={() => setMostrarListaUsuarios(true)}
                      className="w-full pl-12 pr-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    />
                  </div>

                  {mostrarListaUsuarios && busquedaUsuario && (
                    <div className="absolute z-10 w-full mt-2 bg-white border border-gray-200 rounded-xl shadow-xl max-h-64 overflow-y-auto">
                      {loading ? (
                        <div className="p-6 text-center text-gray-500">
                          <i className="ri-loader-4-line animate-spin text-xl"></i>
                          <p className="mt-2 text-sm">Cargando usuarios...</p>
                        </div>
                      ) : usuariosFiltrados.length === 0 ? (
                        <div className="p-6 text-center text-gray-500">
                          <i className="ri-user-search-line text-3xl mb-2"></i>
                          <p className="text-sm">No se encontraron usuarios</p>
                        </div>
                      ) : (
                        usuariosFiltrados.map((usuario) => (
                          <button
                            key={usuario.id}
                            type="button"
                            onClick={() => handleSeleccionarUsuario(usuario)}
                            className="w-full px-4 py-3 hover:bg-gray-50 flex items-center gap-3 border-b border-gray-100 last:border-b-0 text-left transition-colors"
                          >
                            <div className="w-11 h-11 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-sm">
                              <span className="text-sm font-bold text-white">
                                {usuario.nombre.split(' ').map(n => n[0]).join('').slice(0, 2)}
                              </span>
                            </div>
                            <div className="flex-1 min-w-0">
                              <p className="font-semibold text-gray-900 truncate">{usuario.nombre}</p>
                              <p className="text-xs text-gray-500 truncate">{usuario.email}</p>
                            </div>
                            <div className="flex flex-col items-end gap-1">
                              <span className={`px-2.5 py-0.5 rounded-full text-xs font-semibold ${
                                usuario.estado === 'activo' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {usuario.estado}
                              </span>
                              <span className="text-xs text-gray-600 font-medium">{usuario.tipo_membresia}</span>
                            </div>
                          </button>
                        ))
                      )}
                    </div>
                  )}
                </div>
              ) : (
                <div className="flex items-center gap-4 p-4 bg-gradient-to-r from-teal-50 to-teal-50/50 border-2 border-teal-200 rounded-xl">
                  <div className="w-14 h-14 bg-gradient-to-br from-teal-400 to-teal-600 rounded-full flex items-center justify-center flex-shrink-0 shadow-md">
                    <span className="text-lg font-bold text-white">
                      {usuarioSeleccionado.nombre.split(' ').map(n => n[0]).join('').slice(0, 2)}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="font-bold text-gray-900 text-lg">{usuarioSeleccionado.nombre}</p>
                    <p className="text-sm text-gray-600">{usuarioSeleccionado.email}</p>
                    <p className="text-sm text-teal-700 font-semibold mt-0.5">{usuarioSeleccionado.tipo_membresia}</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => {
                      setUsuarioSeleccionado(null);
                      setNotaVencimientoAuto('');
                      setTipoRegistro('membresia');
                      setPlanDestinoId('');
                      setUsarAbonos(false);
                      setFlowPendiente(null);
                      setConsultandoFlow(false);
                      setConciliarFlow(true);
                      setAbonos([nuevoAbono()]);
                      setFormData(prev => ({ ...prev, plan: '', monto: '' }));
                    }}
                    className="px-4 py-2 text-sm font-medium text-teal-700 hover:bg-teal-100 rounded-lg transition-colors"
                  >
                    Cambiar
                  </button>
                </div>
              )}
            </div>

            {usuarioSeleccionado && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Concepto del registro
                </label>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                  <button
                    type="button"
                    onClick={() => handleTipoRegistroChange('membresia')}
                    className={`rounded-xl border-2 p-4 text-left transition-all ${
                      tipoRegistro === 'membresia'
                        ? 'border-teal-500 bg-teal-50 text-teal-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="block font-semibold">Pago de membresía</span>
                    <span className="block text-xs mt-1 opacity-75">Mensualidad o renovación normal</span>
                  </button>
                  <button
                    type="button"
                    onClick={() => handleTipoRegistroChange('cambio_plan')}
                    disabled={!planActual}
                    className={`rounded-xl border-2 p-4 text-left transition-all disabled:opacity-50 disabled:cursor-not-allowed ${
                      tipoRegistro === 'cambio_plan'
                        ? 'border-blue-500 bg-blue-50 text-blue-900'
                        : 'border-gray-200 bg-white text-gray-700 hover:border-gray-300'
                    }`}
                  >
                    <span className="block font-semibold">Cambiar plan</span>
                    <span className="block text-xs mt-1 opacity-75">Cobrar solo la diferencia del upgrade</span>
                  </button>
                </div>
                {!planActual && (
                  <p className="mt-2 text-xs text-amber-700">
                    El plan actual del usuario no está disponible en el catálogo y no permite calcular una diferencia.
                  </p>
                )}
              </div>
            )}

            {usuarioSeleccionado && consultandoFlow && (
              <div className="rounded-xl border border-gray-200 bg-gray-50 p-4 text-sm text-gray-600">
                <i className="ri-loader-4-line mr-2 animate-spin"></i>
                Consultando cobros pendientes en Flow...
              </div>
            )}

            {usuarioSeleccionado && flowPendiente && tipoRegistro === 'membresia' && (
              <div className="rounded-xl border-2 border-amber-300 bg-amber-50 p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-alert-line mt-0.5 text-xl text-amber-700"></i>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-amber-950">Cobro automático pendiente en Flow</p>
                    <p className="mt-1 text-sm text-amber-900">
                      Factura {flowPendiente.invoice_id} por <strong>${Number(flowPendiente.amount).toLocaleString('es-CL')}</strong>
                      {flowPendiente.next_attempt_date ? ` · próximo intento: ${flowPendiente.next_attempt_date}` : ''}.
                    </p>
                    <label className="mt-3 flex cursor-pointer items-start gap-3 rounded-lg border border-amber-200 bg-white/70 p-3">
                      <input
                        type="checkbox"
                        checked={conciliarFlow}
                        onChange={(event) => setConciliarFlow(event.target.checked)}
                        disabled={usarAbonos || formData.estado !== 'Completado'}
                        className="mt-0.5 h-4 w-4 rounded border-amber-400 text-amber-700 focus:ring-amber-500"
                      />
                      <span>
                        <span className="block text-sm font-semibold text-amber-950">Conciliar pago pendiente de Flow</span>
                        <span className="mt-1 block text-xs text-amber-800">
                          Informará esta factura como pagada por otro medio, detendrá sus reintentos y conservará la tarjeta para el próximo ciclo.
                        </span>
                      </span>
                    </label>
                    {usarAbonos && (
                      <p className="mt-2 text-xs font-medium text-amber-800">La conciliación Flow está disponible únicamente con un pago completo en un solo comprobante.</p>
                    )}
                  </div>
                </div>
              </div>
            )}

            {usuarioSeleccionado && flowPagado && tipoRegistro === 'membresia' && (
              <div className="rounded-xl border-2 border-emerald-300 bg-emerald-50 p-4">
                <div className="flex items-start gap-3">
                  <i className="ri-checkbox-circle-line mt-0.5 text-xl text-emerald-700"></i>
                  <div className="flex-1">
                    <p className="text-sm font-bold text-emerald-950">Pago confirmado por Flow</p>
                    <p className="mt-1 text-sm text-emerald-900">
                      Factura {flowPagado.invoice_id} por <strong>${Number(flowPagado.amount).toLocaleString('es-CL')}</strong>. Todavía no está registrada en Mowe.
                    </p>
                    <button
                      type="button"
                      onClick={() => void conciliarPagoRealizadoEnFlow()}
                      disabled={conciliandoPagoFlow}
                      className="mt-3 rounded-lg bg-emerald-700 px-4 py-2.5 text-sm font-bold text-white disabled:opacity-50"
                    >
                      {conciliandoPagoFlow ? <><i className="ri-loader-4-line mr-2 animate-spin"></i>Conciliando...</> : <><i className="ri-refresh-line mr-2"></i>Conciliar pago Flow</>}
                    </button>
                    <p className="mt-2 text-xs text-emerald-800">
                      {flowPagado.membership_will_be_preserved
                        ? `No genera otro cobro ni agrega días: conservará la vigencia hasta el ${flowPagado.membership_end?.split('-').reverse().join('-')}.`
                        : 'No genera otro cobro: importa el pago confirmado por Flow y actualiza la membresía.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {tipoRegistro === 'membresia' ? (
              /* Plan y Monto en dos columnas */
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Plan <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.plan}
                  onChange={(e) => handlePlanChange(e.target.value)}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  required
                >
                  <option value="">Seleccionar plan</option>
                  {planes.map((plan) => (
                    <option key={plan.id} value={plan.nombre}>
                      {plan.nombre} - ${Number(plan.precio).toLocaleString('es-CL')}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  {usarAbonos ? 'Total del plan' : 'Monto'} <span className="text-red-500">*</span>
                </label>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">$</span>
                  <input
                    type="number"
                    value={formData.monto}
                    onChange={(e) => setFormData({ ...formData, monto: e.target.value })}
                    className="w-full pl-8 pr-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                    placeholder="65000"
                    min="0"
                    readOnly={usarAbonos}
                    required
                  />
                </div>
              </div>
              </div>
            ) : (
              <div className="rounded-xl border border-blue-200 bg-blue-50/60 p-4 space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">Plan actual</label>
                    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
                      <p className="text-sm font-semibold text-gray-900">{planActual?.nombre || 'Sin plan'}</p>
                      <p className="text-xs text-gray-500 mt-1">
                        {planActual ? `$${Number(planActual.precio).toLocaleString('es-CL')}` : 'Precio no disponible'}
                      </p>
                    </div>
                  </div>
                  <div>
                    <label className="block text-sm font-semibold text-gray-700 mb-2">
                      Nuevo plan <span className="text-red-500">*</span>
                    </label>
                    <select
                      value={planDestinoId}
                      onChange={(event) => setPlanDestinoId(event.target.value)}
                      className="w-full px-4 py-3 text-sm border border-gray-300 bg-white rounded-xl focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      required
                    >
                      <option value="">Seleccionar nuevo plan</option>
                      {planes
                        .filter((plan) => plan.id !== planActual?.id && normalizePlanName(plan.nombre) !== normalizePlanName(planActual?.nombre))
                        .map((plan) => (
                          <option key={plan.id} value={plan.id}>
                            {plan.nombre} - ${Number(plan.precio).toLocaleString('es-CL')}
                          </option>
                        ))}
                    </select>
                  </div>
                </div>

                {planDestino && planActual && (
                  <div className="grid grid-cols-3 gap-2 rounded-xl border border-blue-100 bg-white p-3 text-center">
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Plan actual</p>
                      <p className="mt-1 text-sm font-semibold text-gray-800">${Number(planActual.precio).toLocaleString('es-CL')}</p>
                    </div>
                    <div>
                      <p className="text-[11px] uppercase tracking-wide text-gray-500">Plan nuevo</p>
                      <p className="mt-1 text-sm font-semibold text-gray-800">${Number(planDestino.precio).toLocaleString('es-CL')}</p>
                    </div>
                    <div className={diferenciaCambioPlan > 0 ? 'text-blue-700' : 'text-amber-700'}>
                      <p className="text-[11px] uppercase tracking-wide">A cobrar</p>
                      <p className="mt-1 text-lg font-bold">${Math.max(0, diferenciaCambioPlan).toLocaleString('es-CL')}</p>
                    </div>
                  </div>
                )}

                {planDestino && diferenciaCambioPlan <= 0 && (
                  <p className="text-sm text-amber-800">
                    Este cambio no genera una diferencia positiva. Los cambios a un plan de igual o menor valor deben gestionarse sin registrar un cobro.
                  </p>
                )}
                <p className="text-xs text-blue-800">
                  El cambio conserva la fecha de vencimiento vigente; no agrega otro mes a la membresía.
                </p>
              </div>
            )}

            {tipoRegistro === 'membresia' && (
              <div className="rounded-xl border border-indigo-200 bg-indigo-50/60 p-4">
                <label className="flex cursor-pointer items-start gap-3">
                  <input
                    type="checkbox"
                    checked={usarAbonos}
                    onChange={(event) => handleUsarAbonos(event.target.checked)}
                    className="mt-1 h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
                  />
                  <span>
                    <span className="block text-sm font-semibold text-indigo-950">Múltiples vouchers o abono parcial</span>
                    <span className="mt-1 block text-xs text-indigo-800">
                      La membresía se activará o renovará una sola vez cuando los abonos completen el valor total del plan.
                    </span>
                  </span>
                </label>
              </div>
            )}

            {tipoRegistro === 'membresia' && usarAbonos && (
              <div className="space-y-4 rounded-xl border border-indigo-200 bg-white p-4">
                <div className="flex items-center justify-between gap-3">
                  <div>
                    <p className="text-sm font-semibold text-gray-900">Vouchers y abonos</p>
                    <p className="text-xs text-gray-500">Cada movimiento conservará su referencia individual.</p>
                  </div>
                  <button
                    type="button"
                    onClick={() => setAbonos((prev) => [...prev, nuevoAbono()])}
                    className="rounded-lg border border-indigo-200 bg-indigo-50 px-3 py-2 text-xs font-semibold text-indigo-700 hover:bg-indigo-100"
                  >
                    <i className="ri-add-line mr-1"></i>Agregar voucher
                  </button>
                </div>

                {abonos.map((abono, index) => (
                  <div key={abono.id} className="rounded-xl border border-gray-200 bg-gray-50 p-4">
                    <div className="mb-3 flex items-center justify-between">
                      <p className="text-sm font-semibold text-gray-800">Voucher o abono {index + 1}</p>
                      {abonos.length > 1 && (
                        <button
                          type="button"
                          onClick={() => quitarAbono(abono.id)}
                          className="text-xs font-semibold text-red-600 hover:text-red-700"
                        >
                          <i className="ri-delete-bin-line mr-1"></i>Quitar
                        </button>
                      )}
                    </div>
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                      <label className="text-xs font-semibold text-gray-600">
                        Monto
                        <input
                          type="number"
                          min="1"
                          value={abono.monto}
                          onChange={(event) => actualizarAbono(abono.id, { monto: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                          required
                        />
                      </label>
                      <label className="text-xs font-semibold text-gray-600">
                        Medio de pago
                        <select
                          value={abono.metodo_pago}
                          onChange={(event) => actualizarAbono(abono.id, { metodo_pago: event.target.value, banco: '' })}
                          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                          required
                        >
                          {metodosPago.map((metodo) => (
                            <option key={metodo.value} value={metodo.value}>{metodo.label}</option>
                          ))}
                        </select>
                      </label>
                      <label className="text-xs font-semibold text-gray-600">
                        N° de comprobante
                        <input
                          type="text"
                          value={abono.numero_comprobante}
                          onChange={(event) => actualizarAbono(abono.id, { numero_comprobante: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                          placeholder="Ej: 184042532171"
                        />
                      </label>
                      <label className="text-xs font-semibold text-gray-600">
                        Fecha
                        <input
                          type="date"
                          value={abono.fecha_pago}
                          onChange={(event) => actualizarAbono(abono.id, { fecha_pago: event.target.value })}
                          className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                          required
                        />
                      </label>
                      {abono.metodo_pago === 'transferencia' && (
                        <label className="text-xs font-semibold text-gray-600 sm:col-span-2">
                          Banco
                          <select
                            value={abono.banco}
                            onChange={(event) => actualizarAbono(abono.id, { banco: event.target.value })}
                            className="mt-1 w-full rounded-lg border border-gray-300 bg-white px-3 py-2.5 text-sm"
                          >
                            <option value="">Seleccionar banco</option>
                            {bancos.map((banco) => <option key={banco} value={banco}>{banco}</option>)}
                          </select>
                        </label>
                      )}
                    </div>
                  </div>
                ))}

                <div className="grid grid-cols-3 gap-2 rounded-xl bg-indigo-50 p-3 text-center">
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Total plan</p>
                    <p className="mt-1 text-sm font-bold text-gray-900">${totalEsperado.toLocaleString('es-CL')}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Abonado</p>
                    <p className="mt-1 text-sm font-bold text-indigo-700">${totalAbonado.toLocaleString('es-CL')}</p>
                  </div>
                  <div>
                    <p className="text-[11px] uppercase tracking-wide text-gray-500">Saldo</p>
                    <p className={`mt-1 text-sm font-bold ${totalAbonado > totalEsperado ? 'text-red-600' : saldoPendiente === 0 ? 'text-green-700' : 'text-amber-700'}`}>
                      ${saldoPendiente.toLocaleString('es-CL')}
                    </p>
                  </div>
                </div>
                {saldoPendiente > 0 && totalAbonado <= totalEsperado && (
                  <p className="text-xs font-medium text-amber-700">
                    Quedará como pago parcial. La vigencia no cambiará hasta completar el saldo.
                  </p>
                )}
              </div>
            )}

            {!(tipoRegistro === 'membresia' && usarAbonos) && (<>
            {/* Método de Pago y Estado */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Método de Pago <span className="text-red-500">*</span>
                </label>
                <select
                  value={formData.metodo_pago}
                  onChange={(e) => setFormData({ ...formData, metodo_pago: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  disabled={metodosPago.length === 0}
                  required
                >
                  {metodosPago.length === 0 && (
                    <option value="">No hay métodos habilitados</option>
                  )}
                  {metodosPago.map((metodo) => (
                    <option key={metodo.value} value={metodo.value}>
                      {metodo.label}
                    </option>
                  ))}
                </select>
                {metodosPago.length === 0 && (
                  <p className="mt-2 text-xs text-amber-700">
                    Habilita al menos un método en Configuración &gt; Métodos de Pago.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Estado del Pago
                </label>
                <select
                  value={formData.estado}
                  onChange={(e) => setFormData({ ...formData, estado: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  disabled={tipoRegistro === 'cambio_plan'}
                  required
                >
                  <option value="Completado">Completado</option>
                  <option value="Pendiente">Pendiente de verificación</option>
                </select>
              </div>
            </div>

            {/* Banco (solo si aplica) */}
            {metodosRequierenBanco.includes(formData.metodo_pago) && (
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Banco
                </label>
                <select
                  value={formData.banco}
                  onChange={(e) => setFormData({ ...formData, banco: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                >
                  <option value="">Seleccionar banco</option>
                  {bancos.map((banco) => (
                    <option key={banco} value={banco}>{banco}</option>
                  ))}
                </select>
              </div>
            )}

            {/* N° de Comprobante y Fecha */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  N° de Comprobante / Referencia
                </label>
                <input
                  type="text"
                  value={formData.numero_comprobante}
                  onChange={(e) => setFormData({ ...formData, numero_comprobante: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  placeholder="Ej: 123456789"
                />
              </div>

              <div>
                <label className="block text-sm font-semibold text-gray-700 mb-2">
                  Fecha del Pago <span className="text-red-500">*</span>
                </label>
                <input
                  type="date"
                  value={formData.fecha_pago}
                  onChange={(e) => setFormData({ ...formData, fecha_pago: e.target.value })}
                  className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent transition-all"
                  required
                />
              </div>
            </div>
            </>)}

            {/* Notas */}
            <div>
              <label className="block text-sm font-semibold text-gray-700 mb-2">
                Notas / Observaciones
              </label>
              <textarea
                value={formData.notas}
                onChange={(e) => {
                  if (e.target.value.length <= 500) {
                    setFormData({ ...formData, notas: e.target.value });
                  }
                }}
                className="w-full px-4 py-3 text-sm border border-gray-300 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-transparent resize-none transition-all"
                rows={3}
                maxLength={500}
                placeholder="Información adicional sobre el pago..."
              />
              <div className="flex items-center justify-between mt-1.5">
                <p className="text-xs text-gray-500">
                  <i className="ri-information-line"></i> Registro de pago manual
                </p>
                <p className="text-xs text-gray-400">{formData.notas.length}/500</p>
              </div>
              {!!notaVencimientoAuto && (
                <p className="text-sm font-semibold text-red-500 mt-1.5">
                  {notaVencimientoAuto}
                </p>
              )}
            </div>

            {/* Info Box */}
            <div className="bg-teal-50 border border-teal-200 rounded-xl p-4 flex gap-3">
              <div className="w-6 h-6 flex items-center justify-center flex-shrink-0">
                <i className="ri-information-line text-teal-600 text-xl"></i>
              </div>
              <div className="flex-1">
                <p className="text-sm font-medium text-teal-900">
                  {tipoRegistro === 'cambio_plan'
                    ? 'Cambio de plan con diferencia'
                    : usarAbonos
                      ? 'Pago de membresía con abonos'
                      : 'Registro de pago manual'}
                </p>
                <p className="text-xs text-teal-700 mt-1">
                  {tipoRegistro === 'cambio_plan'
                    ? 'Al confirmar se registrará el pago, se actualizará el plan del usuario y quedará la trazabilidad del plan anterior, el nuevo y sus valores.'
                    : usarAbonos
                      ? 'Los comprobantes quedarán agrupados. Si existe saldo, el pago seguirá parcial y no cambiará la vigencia de la membresía.'
                      : 'Este registro es para pagos realizados fuera del sistema (transferencias, efectivo en recepción, etc.). El comprobante quedará registrado en el historial del usuario.'}
                </p>
              </div>
            </div>
          </div>
        </form>

        {/* Footer */}
        <div className="px-8 py-5 border-t border-gray-100 bg-gray-50 flex gap-3">
          <button
            type="button"
            onClick={onClose}
            className="flex-1 px-6 py-3 border-2 border-gray-300 text-gray-700 rounded-xl hover:bg-white hover:border-gray-400 transition-all font-semibold"
          >
            Cancelar
          </button>
          <button
            type="submit"
            onClick={handleSubmit}
            disabled={
              guardando ||
              !usuarioSeleccionado ||
              metodosPago.length === 0 ||
              (tipoRegistro === 'membresia' && usarAbonos && (
                !planMembresia || totalAbonado <= 0 || totalAbonado > totalEsperado ||
                abonos.some((abono) => !abono.monto || Number(abono.monto) <= 0 || !abono.metodo_pago || !abono.fecha_pago)
              )) ||
              (tipoRegistro === 'cambio_plan' && (!planDestino || diferenciaCambioPlan <= 0))
            }
            className="flex-1 px-6 py-3 bg-gradient-to-r from-teal-600 to-teal-700 text-white rounded-xl hover:from-teal-700 hover:to-teal-800 transition-all font-semibold disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2 shadow-lg shadow-teal-500/30"
          >
            {guardando ? (
              <>
                <i className="ri-loader-4-line animate-spin"></i>
                Guardando...
              </>
            ) : (
              <>
                <i className="ri-check-line text-lg"></i>
                {tipoRegistro === 'cambio_plan'
                  ? `Cobrar diferencia${diferenciaCambioPlan > 0 ? ` $${diferenciaCambioPlan.toLocaleString('es-CL')}` : ''}`
                  : usarAbonos
                    ? saldoPendiente === 0
                      ? 'Registrar pago completo'
                      : `Registrar abono · saldo $${saldoPendiente.toLocaleString('es-CL')}`
                  : 'Registrar Pago'}
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
}




